
import React from 'react';
import { MapPin, Footprints, Minus, Pentagon, MoreVertical, Trash2, FolderInput, Pencil, Loader2, Star } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { UserContent, WaypointContent, TrackContent, LineContent, AreaContent } from '../../types';
import { formatDistance, formatArea } from '../../services/myContentService';
import { WAYPOINT_ICONS } from '../../services/waypointService';
import { getThumbUrl } from '../../services/firebaseService';

interface MyContentItemProps {
  item: UserContent;
  isSelectMode: boolean;
  isSelected: boolean;
  onToggleSelect: (id: string) => void;
  onTap: (item: UserContent) => void;
  onDelete: (id: string) => void;
  onMoveToFolder?: (id: string) => void;
  onEdit?: (item: UserContent) => void;
  spotPhotos?: Map<string, string>;
  reviewedSpotIds?: Set<string>;
  isBusy?: boolean;
}

function isVisitItem(item: UserContent): boolean {
  return item.id.startsWith('visit_');
}

function getTypeIcon(item: UserContent) {
  switch (item.type) {
    case 'waypoint': {
      if (isVisitItem(item)) return <span className="text-sm">🏕️</span>;
      const meta = WAYPOINT_ICONS.find(w => w.id === (item as WaypointContent).icon);
      return <span className="text-sm">{meta?.emoji ?? '📍'}</span>;
    }
    case 'track':
      return <Footprints size={16} className="text-emerald-500" />;
    case 'line':
      return <Minus size={16} style={{ color: item.color }} strokeWidth={3} />;
    case 'area':
      return <Pentagon size={16} style={{ color: item.color }} />;
  }
}

function getSubtitle(item: UserContent, t: (key: string, opts?: any) => string): string {
  switch (item.type) {
    case 'waypoint': {
      if (isVisitItem(item)) {
        const county = (item as any)._county;
        return county ? t('myContent.visited_with_county', { county }) : t('myContent.visited');
      }
      return t('myContent.waypoint');
    }
    case 'track': {
      const tr = item as TrackContent;
      return `${formatDistance(tr.distance)} · ${Math.round(tr.duration / 60)} min`;
    }
    case 'line':
      return formatDistance((item as LineContent).distance);
    case 'area':
      return formatArea((item as AreaContent).areaSqm);
  }
}

const MyContentItem: React.FC<MyContentItemProps> = ({
  item, isSelectMode, isSelected, onToggleSelect, onTap, onDelete, onMoveToFolder, onEdit, spotPhotos, reviewedSpotIds, isBusy = false,
}) => {
  const { t } = useTranslation();
  const [showMenu, setShowMenu] = React.useState(false);
  const menuRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (!showMenu) return;
    const handleClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setShowMenu(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [showMenu]);

  return (
    <div
      className={`flex items-center gap-3 px-4 py-3 rounded-2xl bg-[var(--bg-input)]/60 border border-[var(--border-primary)] transition-all ${
        isBusy ? 'cursor-wait opacity-80' : 'hover:bg-[var(--bg-input)] active:scale-[0.98] cursor-pointer'
      }`}
      onClick={() => {
        if (isBusy) return;
        if (isSelectMode) onToggleSelect(item.id);
        else onTap(item);
      }}
    >
      {isSelectMode && (
        <div className={`w-5 h-5 rounded-lg border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
          isSelected ? 'bg-emerald-500 border-emerald-500' : 'border-[var(--border-secondary)]'
        }`}>
          {isSelected && <span className="text-white text-2xs font-bold">✓</span>}
        </div>
      )}

      {(() => {
        const photoUrl =
          (item as any).photoUrl
          || (item as any)._photoUrl
          || (isVisitItem(item) ? spotPhotos?.get((item as any)._spotId) : undefined);
        return photoUrl ? (
          <img
            src={getThumbUrl(photoUrl)}
            alt=""
            width={36}
            height={36}
            loading="lazy"
            decoding="async"
            className="w-9 h-9 rounded-xl object-cover flex-shrink-0"
            // Faller tilbake til originalen hvis miniatyren ikke finnes ennå. Det er
            // en dyr fallback (originalene er ofte flere MB), så lista lastes lat —
            // rader utenfor skjermen henter ingenting før de rulles inn.
            onError={(e) => { const t = e.target as HTMLImageElement; if (t.src !== photoUrl) t.src = photoUrl; }}
          />
        ) : (
          <div
            className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: item.color + '20' }}
          >
            {isBusy ? <Loader2 size={16} className="text-emerald-400 animate-spin" /> : getTypeIcon(item)}
          </div>
        );
      })()}

      <div className="flex-1 min-w-0">
        <p className="text-[12px] font-bold text-[var(--text-primary)] truncate">{item.name}</p>
        <p className="text-2xs text-[var(--text-secondary)]">{getSubtitle(item, t)}</p>
        {isVisitItem(item) && reviewedSpotIds && !(reviewedSpotIds.has((item as any)._spotId)) && (
          <span className="inline-flex items-center gap-1 text-3xs font-bold text-amber-400 mt-0.5">
            <Star size={9} /> {t('myContent.missing_review', 'Mangler anmeldelse')}
          </span>
        )}
      </div>

      {!isSelectMode && !isVisitItem(item) && !isBusy && (
        <div className="relative flex-shrink-0" ref={menuRef}>
          <button
            onClick={(e) => { e.stopPropagation(); setShowMenu(!showMenu); }}
            className="p-1.5 text-[var(--text-muted)] hover:text-[var(--text-secondary)] rounded-lg hover:bg-[var(--bg-surface-alt)]"
          >
            <MoreVertical size={14} />
          </button>
          {showMenu && (
            <div className="absolute right-0 top-full mt-1 bg-[var(--bg-input)] rounded-xl border border-[var(--border-secondary)] shadow-xl overflow-hidden z-20 min-w-[140px]">
              {onEdit && (
                <button
                  onClick={(e) => { e.stopPropagation(); setShowMenu(false); onEdit(item); }}
                  className="w-full flex items-center gap-2 px-3 py-2.5 text-tiny text-[var(--text-secondary)] hover:bg-[var(--bg-surface-alt)]"
                >
                  <Pencil size={12} /> {t('myContent.edit')}
                </button>
              )}
              {onMoveToFolder && (
                <button
                  onClick={(e) => { e.stopPropagation(); setShowMenu(false); onMoveToFolder(item.id); }}
                  className="w-full flex items-center gap-2 px-3 py-2.5 text-tiny text-[var(--text-secondary)] hover:bg-[var(--bg-surface-alt)]"
                >
                  <FolderInput size={12} /> {t('myContent.move_to_folder')}
                </button>
              )}
              <button
                onClick={(e) => { e.stopPropagation(); setShowMenu(false); onDelete(item.id); }}
                className="w-full flex items-center gap-2 px-3 py-2.5 text-tiny text-red-400 hover:bg-[var(--bg-surface-alt)]"
              >
                <Trash2 size={12} /> {t('myContent.delete')}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default React.memo(MyContentItem);
